import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 10;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 */
class HeaderMenu extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const overflow = this.overflowMenu;
    if (overflow instanceof HTMLElement) {
      overflow.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
        signal: this.#abortController.signal,
      });
    }

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {{ activeItem: HTMLElement | null }}
   */
  #state = {
    activeItem: null,
  };

  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * @returns {HTMLElement | null}
   */
  get overflowMenu() {
    const overflowList = this.refs.overflowMenu;
    if (!overflowList) return null;

    // Safety check for array or single element
    const el = Array.isArray(overflowList) ? overflowList[0] : overflowList;
    if (!(el instanceof HTMLElement)) return null;

    return /** @type {HTMLElement | null} */ (el.shadowRoot?.querySelector('[part="overflow"]'));
  }

  get overflowHovered() {
    const overflowList = this.refs.overflowMenu;
    if (!overflowList) return false;
    const el = Array.isArray(overflowList) ? overflowList[0] : overflowList;
    return (el instanceof HTMLElement) && el.matches(':hover');
  }

  /**
   * Check if this is a mega menu item
   * @param {Element} target
   * @returns {boolean}
   */
  #isMegaMenuItem(target) {
    const item = target.closest('.menu-list__list-item');
    return !!(item && item.querySelector('.mega-menu'));
  }

  /**
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // ✅ COMPLETE BYPASS: If this is a mega menu item, do nothing
    if (this.#isMegaMenuItem(target)) {
      this.#debouncedDeactivate.cancel();
      this.#debouncedActivateHandler.cancel();
      return;
    }

    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // ✅ Double check: Skip mega menu items
    if (this.#isMegaMenuItem(target)) return;

    this.#debouncedDeactivate.cancel();
    this.dispatchEvent(new MegaMenuHoverEvent());
    this.removeAttribute('data-animating');

    let item = findMenuItem(target);
    if (!item) return;

    if (item == this.#state.activeItem) return;

    const isDefaultSlot = target.getAttribute('slot') === null || target.getAttribute('slot') === '';
    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    if (this.#state.activeItem) {
      this.#state.activeItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;
    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // ✅ BYPASS: Never deactivate mega menu items
    if (this.#isMegaMenuItem(target)) return;

    this.#debouncedActivateHandler.cancel();

    const item = findMenuItem(target);

    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    // ✅ CRITICAL: Never reset height if mega menu is locked
    if (this.style.getPropertyValue('--submenu-height-lock') === 'true') {
      return;
    }

    if (!item || item != this.#state.activeItem) return;

      // 🚫 Never deactivate if pointer is anywhere inside mega menu
      if (this.matches('[data-mega-menu-active="true"]')) {
        return;
      }

    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150));
  };

  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;
  if (element.matches('[slot="more"')) {
    const parent = element.parentElement;
    return parent ? findMenuItem(parent.querySelector('[slot="overflow"]')) : null;
  }
  const link = element.closest('[ref="menuitem"]');
  if (link instanceof HTMLElement) return link;
  const child = element.querySelector('[ref="menuitem"]');
  return child instanceof HTMLElement ? child : null;
}

/**
 * @param {HTMLElement} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}